/**
 * Secure logging utility to prevent log injection attacks
 * Sanitizes user inputs before logging
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

class SecureLogger {
  private isDevelopment = process.env.NODE_ENV === 'development';
  
  /**
   * Sanitize input to prevent log injection
   */
  private sanitize(input: unknown): string {
    if (typeof input === 'string') {
      return input.replace(/[\r\n\t]/g, '_').substring(0, 1000);
    }
    
    if (typeof input === 'object' && input !== null) {
      try {
        return JSON.stringify(input).replace(/[\r\n\t]/g, '_').substring(0, 1000);
      } catch {
        return '[Object]';
      }
    }
    
    return String(input).replace(/[\r\n\t]/g, '_').substring(0, 1000);
  }

  /**
   * Log with specified level
   */
  private log(level: LogLevel, message: string, data?: unknown): void {
    if (!this.isDevelopment) return;
    
    const sanitizedMessage = this.sanitize(message);
    const timestamp = new Date().toISOString();
    
    if (data !== undefined) {
      const sanitizedData = this.sanitize(data);
      console[level](`[${timestamp}] ${sanitizedMessage}`, sanitizedData);
    } else {
      console[level](`[${timestamp}] ${sanitizedMessage}`);
    }
  }

  debug(message: string, data?: unknown): void {
    this.log('debug', message, data);
  }

  info(message: string, data?: unknown): void {
    this.log('info', message, data);
  }

  warn(message: string, data?: unknown): void {
    this.log('warn', message, data);
  }

  error(message: string, data?: unknown): void {
    this.log('error', message, data);
  }
}

export const secureLogger = new SecureLogger();